import { useEffect, /*useMemo,*/ useState } from 'react';
import "../../../styles/DashboardStyles/DashboardContent/DashboardApp.css";
import {
  collection,
  onSnapshot,
  deleteDoc,
  doc,
  addDoc,
  Timestamp,
  updateDoc,
  arrayUnion,
} from "firebase/firestore";
import { db } from "../../../firebase/config"; // update path to your firebase config


type Status =
  | "submitted"
  | "under_review"
  | "acknowledged"
  | "evaluation"
  | "approved"
  | "rejected"
  | "issued"
  | "completed";

  /*
    submitted — The application has just been received from the client. Nothing has been opened or looked at yet by Kole Medical staff.
    under_review — Kole Medical (or SAHPRA) is actively checking the application and supporting documents for completeness and correctness.
    acknowledged — SAHPRA has formally received and logged the application into their system. This is often a separate confirmation step from the initial submission.
    evaluation — SAHPRA's technical evaluators are assessing the application against regulatory requirements (this stage can take the longest).
    approved — SAHPRA has approved the application, but the physical/official license document hasn't been issued yet.
    rejected — The application was declined. Usually requires resubmission, corrections, or an appeal.
    issued — The official license certificate has been generated and issued to the client.
    completed — The full process is closed out — license issued, client notified, all paperwork filed. End of the workflow.
   */

    type LicenseType = "Distribution" | "Wholesale" | "Manufacturing";

interface Application {
  id: string;
  applicantName: string;
  licenseType: LicenseType;
  status: Status;
  dateSubmitted: string; // ISO date string e.g. "2024-04-12"
}

interface User {
  id: string;
  fullName: string;
  email: string;
  accountStatus: "active" | "suspended";
}

// ── EMPTY FORM STATE ─────────────────────────────────────────────────────────
 
const emptyForm = {
  userId: "",
  licenseType: "" as LicenseType | "",
};

export default function DashboardApp() {
    const [applications, setApplications] = useState<Application[]>([]);
    const [users, setUsers] = useState<User[]>([]);

    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState("");
    //const [sortDirection, setSortDirection] = useState<"asc" | "desc" | null>(null);
    const [selectedApp, setSelectedApp] = useState<Application | null>(null);

    const [editStatus, setEditStatus] = useState<Status | "">("");
    const [editNote, setEditNote] = useState("");
    const [editSubmitting, setEditSubmitting] = useState(false);
    const [editError, setEditError] = useState("");
    const [editSuccess, setEditSuccess] = useState(false);

    const [form, setForm] = useState(emptyForm);
     const [formError, setFormError] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);


    // ── FETCH APPLICATIONS (real-time) ────────────────────────────────────────
 
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "applications"),
      (snapshot) => {
        const data: Application[] = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as Omit<Application, "id">),
        }));
        setApplications(data);
        setLoading(false);
      },
      (error) => {
        console.error("Error fetching applications:", error);
        setLoading(false);
      }
    );
 
    return () => unsubscribe();
  }, []);

   // ── FETCH USERS (real-time) ───────────────────────────────────────────────
 
  useEffect(() => {
    const unsubscribe = onSnapshot(
      collection(db, "users"),
      (snapshot) => {
        const data: User[] = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...(doc.data() as Omit<User, "id">),
        }));
        // Only active users can have applications
        setUsers(data.filter((u) => u.accountStatus === "active"));
      },
      (error) => {
        console.error("Error fetching users:", error);
      }
    );
 
    return () => unsubscribe();
  }, []);

    // ── ADD APPLICATION ───────────────────────────────────────────────────────
 
  const handleAddApplication = async () => {
    setFormError("");
 
    if (!form.userId) {
      setFormError("Please select an existing user.");
      return;
    }
    if (!form.licenseType) {
      setFormError("Please select a license type.");
      return;
    }
 
    const selectedUser = users.find((u) => u.id === form.userId);
    if (!selectedUser) {
      setFormError("Selected user not found.");
      return;
    }
 
    setSubmitting(true);
 
    try {
      await addDoc(collection(db, "applications"), {
        userId: selectedUser.id,
        applicantName: selectedUser.fullName,
        licenseType: form.licenseType,
        status: "submitted" as Status,
        dateSubmitted: new Date().toISOString().split("T")[0],
        createdAt: Timestamp.now(),
        statusHistory: [
          {
            step: "submitted",
            date: Timestamp.now(),
            note: "Application created by admin.",
          },
        ],
      });
 
      setForm(emptyForm);
      setShowModal(false);
    } catch (error) {
      console.error("Error adding application:", error);
      setFormError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── CLOSE MODAL ───────────────────────────────────────────────────────────
 
  const handleCloseModal = () => {
    setShowModal(false);
    setForm(emptyForm);
    setFormError("");
  };


    const filteredApplications = applications.filter((app) => {
      // Normalize search input — lowercase and trim whitespace
      const query = search.toLowerCase().trim();

      // If search is empty, show all applications
      if (!query) return true;

      // Check each field — returns true if ANY field matches
      return (
        app.applicantName.toLowerCase().includes(query) ||
        app.licenseType.toLowerCase().includes(query) ||
        app.status.replace(/_/g, " ").toLowerCase().includes(query)
      );
    }); 

    // Update an application's status
    /*const handleStatusChange = (id: string, newStatus: Status) => {
        setApplications(prev =>
        prev.map(app => (app.id === id ? { ...app, status: newStatus } : app))
        );
    };*/

    // ── UPDATE STATUS IN FIREBASE ─────────────────────────────────────────────────
 
const handleUpdateStatus = async () => {
  if (!selectedApp) return;
  if (!editStatus) { setEditError("Please select a status."); return; }
  if (editStatus === selectedApp.status) {
    setEditError("This is already the current status.");
    return;
  }
 
  setEditSubmitting(true);
  setEditError("");
 
  try {
    const appRef = doc(db, "applications", selectedApp.id);
 
    await updateDoc(appRef, {
      status: editStatus,
      lastUpdated: Timestamp.now(),
      statusHistory: arrayUnion({
        step: editStatus,
        date: Timestamp.now(),
        note: editNote.trim() || `Status updated to ${editStatus}.`,
      }),
    });
 
    // Update local selectedApp so the modal reflects the new status
    setSelectedApp((prev) => prev ? { ...prev, status: editStatus as Status } : prev);
    setEditSuccess(true);
    setEditNote("");
 
    // Auto-hide success message after 2.5s
    setTimeout(() => setEditSuccess(false), 2500);
 
  } catch (error) {
    console.error("Error updating status:", error);
    setEditError("Failed to update. Please try again.");
  } finally {
    setEditSubmitting(false);
  }
};

 
// ── STATUS CONFIG (put this outside the component) ────────────────────────────
 
const STATUS_OPTIONS: { value: Status; label: string; color: string }[] = [
  { value: "submitted",    label: "Submitted",    color: "#007BFF" },
  { value: "under_review", label: "Under Review", color: "#FD7E14" },
  { value: "acknowledged", label: "Acknowledged", color: "#6C757D" },
  { value: "evaluation",   label: "Evaluation",   color: "#856404" },
  { value: "approved",     label: "Approved",     color: "#28A745" },
  { value: "rejected",     label: "Rejected",     color: "#DC3545" },
  { value: "issued",       label: "Issued",       color: "#024498" },
  { value: "completed",    label: "Completed",    color: "#145a32" },
];

     // ── DELETE APPLICATION ────────────────────────────────────────────────────
 
  const handleDelete = async (id: string) => {
    try {
      await deleteDoc(doc(db, "applications", id));
      setDeleteConfirmId(null);
    } catch (error) {
      console.error("Error deleting application:", error);
    }
  };


  const handleView = (id: string) => {
  const app = applications.find((a) => a.id === id);
  if (!app) return;
  setSelectedApp(app);
  setEditStatus(app.status);
  setEditNote("");
  setEditError("");
  setEditSuccess(false);
  setShowEditModal(true);
};
 
const handleCloseEditModal = () => {
  setShowEditModal(false);
  setSelectedApp(null);
  setEditStatus("");
  setEditNote("");
  setEditError("");
  setEditSuccess(false);
};


    // Toggle sort: null -> asc -> desc -> null
    /*const handleSortClick = () => {
        setSortDirection(prev => (prev === "asc" ? "desc" : prev === "desc" ? null : "asc"));
    };*/

    // Filter + sort applications
    /*const statusLabels: Record<Status, string> = {
        submitted: "Submitted",
        under_review: "Under Review",
        acknowledged: "Acknowledged",
        evaluation: "Evaluation",
        approved: "Approved",
        rejected: "Rejected",
        issued: "Issued",
        completed: "Completed",
        };

        /*const visibleApplications = useMemo(() => {
        const query = search.toLowerCase();

        let result = applications.filter(app =>
            app.applicantName.toLowerCase().includes(query) ||
            app.licenseType.toLowerCase().includes(query) ||
            app.status.toLowerCase().includes(query) ||
            statusLabels[app.status].toLowerCase().includes(query)
        );

        if (sortDirection) {
            result = [...result].sort((a, b) => {
            const cmp = a.status.localeCompare(b.status);
            return sortDirection === "asc" ? cmp : -cmp;
            });
        }

        return result;
        }, [applications, search, sortDirection]);*/

    return (
    <div className="dashboard-users">
 
      {/* Header */}
      <div className="du-header">
        <div>
          <h1 className="du-title">Applications</h1>
          <div className="du-search-wrapper">
            <input
              type="text"
              placeholder="Search by name, license type or status..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {/* Only show ✕ when there is something typed */}
            {search && (
              <button
                className="du-search-clear"
                onClick={() => setSearch("")}
              >
                ✕
              </button>
            )}
          </div>
          <p className="du-subtitle">
            {applications.length} total application{applications.length !== 1 ? "s" : ""}
          </p>
        </div>
        <button className="du-add-btn" onClick={() => setShowModal(true)}>
          + Add Application
        </button>
      </div>
 
         {/* Table */}
      {loading ? (
        <div className="du-loading">
          <span className="du-spinner" />
          Loading applications...
        </div>
 
      ) : applications.length === 0 ? (
        <div className="du-empty">
          <span className="du-empty-icon">📋</span>
          <p>No applications found.</p>
          <button className="du-add-btn" onClick={() => setShowModal(true)}>
            + Add First Application
          </button>
        </div>
 
      ) : (
        <>
          {/* ── DESKTOP TABLE (hidden on mobile) ── */}
          <div className="du-table-wrapper">
            <table className="du-table">
              <thead>
                <tr>
                  <th>Applicant</th>
                  <th>License Type</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredApplications.length > 0 ? (
                  filteredApplications.map((app) => (
                  <tr key={app.id}>
 
                    {/* Applicant — name + ID below */}
                    <td>
                      <div className="du-applicant">
                        <div className="du-applicant-avatar">
                          {app.applicantName.charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <div className="du-applicant-name">{app.applicantName}</div>
                          <div className="du-applicant-id">{app.id}</div>
                        </div>
                      </div>
                    </td>
 
                    <td>
                      <span className="du-license-tag">{app.licenseType}</span>
                    </td>
 
                    <td>
                      <span className={`du-status du-status-${app.status}`}>
                        {app.status.replace(/_/g, " ")}
                      </span>
                    </td>
 
                    <td className="du-date">{app.dateSubmitted}</td>
 
                    {/* Actions */}
                    <td className="du-actions">
                      {deleteConfirmId === app.id ? (
                        <div className="du-confirm">
                          <span>Delete?</span>
                          <button
                            className="du-btn-danger-sm"
                            onClick={() => handleDelete(app.id)}
                          >
                            Yes
                          </button>
                          <button
                            className="du-btn-ghost-sm"
                            onClick={() => setDeleteConfirmId(null)}
                          >
                            No
                          </button>
                        </div>
                      ) : (
                        <div className="du-action-group">
                          <button
                            className="du-btn-view"
                            onClick={() => handleView(app.id)}
                            title="View / Edit"
                          >
                            ✏ View
                          </button>
                          <button
                            className="du-btn-delete-icon"
                            onClick={() => setDeleteConfirmId(app.id)}
                            title="Delete"
                          >
                            🗑
                          </button>
                        </div>
                      )}
                    </td>
 
                  </tr>
                ))
                  ) : (
                    // Empty state when nothing matches
                    <tr>
                      <td colSpan={5} className="du-empty">
                        No applications found for "<strong>{search}</strong>"
                      </td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>
 
          {/* ── MOBILE CARDS (hidden on desktop) ── */}
          <div className="du-card-list">
            {filteredApplications.length > 0 ? (
              filteredApplications.map((app) => (
                <div className="du-card" key={app.id}>
 
                {/* Card Top */}
                <div className="du-card-top">
                  <div className="du-applicant">
                    <div className="du-applicant-avatar">
                      {app.applicantName.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="du-applicant-name">{app.applicantName}</div>
                      <div className="du-applicant-id">{app.id}</div>
                    </div>
                  </div>
                  <span className={`du-status du-status-${app.status}`}>
                    {app.status.replace(/_/g, " ")}
                  </span>
                </div>
 
                {/* Card Meta */}
                <div className="du-card-meta">
                  <div className="du-card-meta-item">
                    <span className="du-meta-label">License</span>
                    <span className="du-license-tag">{app.licenseType}</span>
                  </div>
                  <div className="du-card-meta-item">
                    <span className="du-meta-label">Submitted</span>
                    <span className="du-meta-value">{app.dateSubmitted}</span>
                  </div>
                </div>
 
                {/* Card Actions */}
                <div className="du-card-actions">
                  {deleteConfirmId === app.id ? (
                    <div className="du-confirm">
                      <span>Are you sure?</span>
                      <button
                        className="du-btn-danger-sm"
                        onClick={() => handleDelete(app.id)}
                      >
                        Yes, Delete
                      </button>
                      <button
                        className="du-btn-ghost-sm"
                        onClick={() => setDeleteConfirmId(null)}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <>
                      <button
                        className="du-btn-view full"
                        onClick={() => handleView(app.id)}
                      >
                        ✏ View / Edit
                      </button>
                      <button
                        className="du-btn-delete-icon"
                        onClick={() => setDeleteConfirmId(app.id)}
                        title="Delete"
                      >
                        🗑
                      </button>
                    </>
                  )}
                </div>
 
              </div>
              ))
                ) : (
                  // Empty state when nothing matches
                  <div className="du-card du-card-empty">
                    <p className="du-empty">
                      No applications found for "<strong>{search}</strong>"
                    </p>
                  </div>
                )}
          </div>
        </>
      )}
 
      {/* ── MODAL OVERLAY ── */}
      {showModal && (
        <div className="du-overlay" onClick={handleCloseModal}>
          <div
            className="du-modal"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="du-modal-header">
              <div>
                <h2 className="du-modal-title">New Application</h2>
                <p className="du-modal-subtitle">
                  Create an application for an existing user
                </p>
              </div>
              <button className="du-modal-close" onClick={handleCloseModal}>
                ✕
              </button>
            </div>
 
            {/* Modal Body */}
            <div className="du-modal-body">
 
              {/* User Select */}
              <div className="du-field">
                <label className="du-label">
                  Select User <span className="du-req">*</span>
                </label>
                {users.length === 0 ? (
                  <p className="du-no-users">
                    ⚠ No active users found in the system.
                  </p>
                ) : (
                  <select
                    className="du-select"
                    value={form.userId}
                    onChange={(e) =>
                      setForm((prev) => ({ ...prev, userId: e.target.value }))
                    }
                  >
                    <option value="">— Choose an existing user —</option>
                    {users.map((user) => (
                      <option key={user.id} value={user.id}>
                        {user.fullName} — {user.email}
                      </option>
                    ))}
                  </select>
                )}
                <p className="du-field-hint">
                  Only active registered users are listed here.
                </p>
              </div>
 
              {/* License Type */}
              <div className="du-field">
                <label className="du-label">
                  License Type <span className="du-req">*</span>
                </label>
                <select
                  className="du-select"
                  value={form.licenseType}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      licenseType: e.target.value as LicenseType,
                    }))
                  }
                >
                  <option value="">— Choose a license type —</option>
                  <option value="Distribution">Distribution License</option>
                  <option value="Wholesale">Wholesale License</option>
                  <option value="Manufacturing">Manufacturing License</option>
                </select>
              </div>
 
              {/* Auto-filled info */}
              <div className="du-info-box">
                <p>📋 Status will be set to <strong>Submitted</strong> automatically.</p>
                <p>📅 Date will be set to <strong>today</strong> automatically.</p>
              </div>
 
              {/* Error */}
              {formError && (
                <div className="du-form-error">⚠ {formError}</div>
              )}
            </div>
 
            {/* Modal Footer */}
            <div className="du-modal-footer">
              <button className="du-btn-ghost" onClick={handleCloseModal}>
                Cancel
              </button>
              <button
                className="du-btn-primary"
                onClick={handleAddApplication}
                disabled={submitting}
              >
                {submitting ? (
                  <><span className="du-spinner small" /> Saving...</>
                ) : (
                  "Create Application"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── EDIT MODAL OVERLAY ── */} 
      {showEditModal && selectedApp && (
        <div className="du-overlay" onClick={handleCloseEditModal}>
          <div className="du-modal du-modal-lg" onClick={(e) => e.stopPropagation()}>
      
            {/* Modal Header */}
            <div className="du-modal-header">
              <div>
                <h2 className="du-modal-title">Application Details</h2>
                <p className="du-modal-subtitle">{selectedApp.id}</p>
              </div>
              <button className="du-modal-close" onClick={handleCloseEditModal}>✕</button>
            </div>
      
            {/* Modal Body */}
            <div className="du-modal-body">
      
              {/* ── APPLICANT INFO CARD ── */}
              <div className="du-detail-card">
                <div className="du-applicant">
                  <div className="du-applicant-avatar du-avatar-lg">
                    {selectedApp.applicantName.charAt(0).toUpperCase()}
                  </div>
                  <div>
                    <div className="du-applicant-name">{selectedApp.applicantName}</div>
                    <div className="du-applicant-id">{selectedApp.id}</div>
                  </div>
                </div>
      
                {/* Detail grid */}
                <div className="du-detail-grid">
                  <div className="du-detail-item">
                    <span className="du-meta-label">License Type</span>
                    <span className="du-license-tag">{selectedApp.licenseType}</span>
                  </div>
                  <div className="du-detail-item">
                    <span className="du-meta-label">Date Submitted</span>
                    <span className="du-meta-value">{selectedApp.dateSubmitted}</span>
                  </div>
                  <div className="du-detail-item">
                    <span className="du-meta-label">Current Status</span>
                    <span className={`du-status du-status-${selectedApp.status}`}>
                      {selectedApp.status.replace(/_/g, " ")}
                    </span>
                  </div>
                </div>
              </div>
      
              {/* ── STATUS PROGRESS TRACKER ── */}
              <div className="du-progress-section">
                <p className="du-section-label">Application Progress</p>
                <div className="du-progress-track">
                  {STATUS_OPTIONS.map((s, i) => {
                    const currentIndex = STATUS_OPTIONS.findIndex(
                      (o) => o.value === selectedApp.status
                    );
                    const isDone    = i < currentIndex;
                    const isCurrent = i === currentIndex;
                    return (
                      <div key={s.value} className="du-progress-step">
                        <div
                          className={`du-progress-dot ${
                            isDone ? "done" : isCurrent ? "current" : ""
                          }`}
                          style={isCurrent ? { borderColor: s.color, background: s.color } : {}}
                        />
                        <span
                          className={`du-progress-label ${isCurrent ? "current" : ""}`}
                          style={isCurrent ? { color: s.color } : {}}
                        >
                          {s.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
      
              {/* ── DIVIDER ── */}
              <hr className="du-divider" />
      
              {/* ── UPDATE STATUS ── */}
              <p className="du-section-label">Update Status</p>
      
              {/* Status radio buttons */}
              <div className="du-status-grid">
                {STATUS_OPTIONS.map((s) => (
                  <label
                    key={s.value}
                    className={`du-status-option ${editStatus === s.value ? "selected" : ""}`}
                    style={editStatus === s.value ? {
                      borderColor: s.color,
                      background: `${s.color}12`,
                    } : {}}
                  >
                    <input
                      type="radio"
                      name="status"
                      value={s.value}
                      checked={editStatus === s.value}
                      onChange={() => {
                        setEditStatus(s.value);
                        setEditError("");
                        setEditSuccess(false);
                      }}
                    />
                    <span
                      className="du-status-option-dot"
                      style={{ background: s.color }}
                    />
                    <span
                      className="du-status-option-label"
                      style={editStatus === s.value ? { color: s.color, fontWeight: 700 } : {}}
                    >
                      {s.label}
                    </span>
                  </label>
                ))}
              </div>
      
              {/* Note field */}
              <div className="du-field">
                <label className="du-label">Add a Note (optional)</label>
                <textarea
                  className="du-textarea"
                  placeholder="e.g. Documents verified. Proceeding to evaluation stage."
                  value={editNote}
                  rows={3}
                  onChange={(e) => setEditNote(e.target.value)}
                />
                <p className="du-field-hint">
                  This note will be saved to the application's status history.
                </p>
              </div>
      
              {/* Success message */}
              {editSuccess && (
                <div className="du-form-success">
                  ✓ Status updated successfully.
                </div>
              )}
      
              {/* Error message */}
              {editError && (
                <div className="du-form-error">⚠ {editError}</div>
              )}
            </div>
      
            {/* Modal Footer */}
            <div className="du-modal-footer">
              <button className="du-btn-ghost" onClick={handleCloseEditModal}>
                Close
              </button>
              <button
                className="du-btn-primary"
                onClick={handleUpdateStatus}
                disabled={editSubmitting}
              >
                {editSubmitting ? (
                  <><span className="du-spinner small" /> Saving...</>
                ) : (
                  "Save Changes"
                )}
              </button>
            </div>
      
          </div>
        </div>
      )}
    </div>
  );
}